import { useEffect, useState } from 'react'
import {
  readThemeChoice,
  saveThemeChoice,
  watchSystemTheme,
  type ThemeChoice,
} from '../../lib/theme.js'
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CreditCard,
  GraduationCap,
  LayoutGrid,
  LifeBuoy,
  Menu,
  Settings2,
  Sparkles,
  Stethoscope,
  Users,
  History,
} from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { Button } from '../ui/Button.js'
import { Avatar } from '../ui/Avatar.js'
import { Text } from '../ui/Typography.js'

import { Tooltip, TooltipProvider } from '../ui/Tooltip.js'
import { ProfileMenu } from './ProfileMenu.js'
import { useMaybeCan } from './Permissions.js'

/**
 * A rail item's icon, by name.
 *
 * Keyed by a string rather than held as an element so `core/constants.ts` stays
 * plain data — it is a `.ts` file and cannot hold JSX, and a rail that has to be
 * assembled in a component is a rail two places have to agree about.
 *
 * These are interface icons, not the brand's fifty-eight: those are a category's
 * own mark, chosen by the client in Settings, and borrowing one for navigation
 * would make the rail change meaning the day somebody edits a category.
 */
const RAIL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  overview: LayoutGrid,
  members: Users,
  providers: Stethoscope,
  bookings: CalendarDays,
  payments: CreditCard,
  classes: GraduationCap,
  events: Sparkles,
  support: LifeBuoy,
  settings: Settings2,
  activity: History,
}

/** Remembered per browser, because it is a preference about this screen. */
const COLLAPSED_KEY = 'hi.rail.collapsed'

export interface NavSection {
  key: string
  label: string
  /** A key into `RAIL_ICONS`. Plain data, so the rail can live in a `.ts` file. */
  icon?: string
  /**
   * The heading this item sits under.
   *
   * Nine items in one list said nothing about which of them is the day's work.
   * Items with no group are drawn first, ungrouped.
   */
  group?: string
  /** A count beside the label — how many need attention. Hidden when zero. */
  badge?: number
  /**
   * The permission this needs. A section somebody cannot open is **not drawn**:
   * the server refuses besides, but finding out by pressing means the screen told
   * them the wrong thing first.
   */
  permission?: string
}

export interface AppShellProps {
  sections: NavSection[]
  current: string
  onNavigate: (key: string) => void
  /**
   * Returns false for a section this person cannot open.
   *
   * Optional: with a `<PermissionProvider>` above, the rail reads from the same
   * place every button on every screen reads from, and the two cannot disagree.
   * Pass this only to override that.
   */
  can?: (permission: string) => boolean
  user: { fullName: string; roleName: string } | null
  onSignOut: () => void
  /** Opens the person's own profile — their name. */
  onOpenProfile?: () => void
  /** Opens the change-password form, which is a different act. */
  onChangePassword?: () => void
  children: React.ReactNode
}

/**
 * The frame: a rail that stays put, and a column that scrolls.
 *
 * **The tooltip provider belongs here, not at the app's root.**
 *
 * `RelativeTime` renders a tooltip, and `_at` timestamps are on nearly every table
 * the desk has. Radix's tooltip **throws** without a provider above it, so without
 * this a table of bookings takes the whole screen down — and the failure appears in
 * the component that is merely showing a date, nowhere near the missing wrapper.
 *
 * Found by assembling a screen out of the layer rather than by reading it: nothing
 * in the source says "this needs a provider", and six passes of reading missed it.
 * A provider somebody has to remember is a provider somebody will forget, so both
 * layouts carry one and no screen has to know.
 */
export function AppShell({
  sections,
  current,
  onNavigate,
  can,
  user,
  onSignOut,
  onOpenProfile,
  onChangePassword,
  children,
}: AppShellProps) {
  /**
   * The theme this browser is set to.
   *
   * State here rather than in the store: it is a preference about this
   * machine, not about the session — it survives signing out, and it must
   * already be applied before React runs, which `index.html` does.
   */
  const [theme, setTheme] = useState<ThemeChoice>(() => readThemeChoice())
  const choose = (next: ThemeChoice) => {
    setTheme(next)
    saveThemeChoice(next)
  }
  /* On "Match my computer", follow it while the desk is open — somebody who
     switches their Mac to dark at sunset should not have to reload. */
  useEffect(() => watchSystemTheme(() => theme), [theme])

  const fromProvider = useMaybeCan()
  const allowed = can ?? fromProvider
  if (!allowed) {
    // Neither a prop nor a provider. Drawing the whole rail would show sections
    // this person cannot open; drawing none would look like a locked-down account.
    // Neither is an answer, so say what is actually wrong.
    throw new Error('AppShell needs a `can` prop or a <PermissionProvider> above it')
  }
  const visible = sections.filter((s) => !s.permission || allowed(s.permission))

  /**
   * Narrowed to icons, and remembered.
   *
   * Asked for directly: *"a state when I can minimize it to icon level and
   * tooltip on hover"*. It is a preference about this screen on this machine,
   * so it lives in the browser rather than on the account — somebody working
   * on a laptop and a large monitor wants a different answer on each.
   */
  const [collapsed, setCollapsed] = useState(false)
  /** Whether the rail is slid over the page. Only meaningful below `lg`. */
  const [railOpen, setRailOpen] = useState(false)
  /**
   * Whether the rail is a column beside the page rather than a drawer over it.
   *
   * `collapsed` is a preference about the **column**: sixty pixels of icons
   * instead of two hundred and forty. A drawer has no such choice — it is
   * open or it is not — so below `lg` a rail that remembered being collapsed
   * slid out at its full width and drew icons with no labels, with a handle
   * on its edge offering to narrow something that was already covering the
   * page. Reported directly: *"even in mobile mode the minimize button is
   * still there and making the sidebar weird when minimized"*.
   *
   * Tailwind cannot express this: the labels are **content**, not styling, so
   * the breakpoint has to be a value React can read.
   */
  const [beside, setBeside] = useState(true)
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 1024px)')
    const sync = () => setBeside(wide.matches)
    sync()
    wide.addEventListener('change', sync)
    return () => wide.removeEventListener('change', sync)
  }, [])
  /** Collapsed only counts where the rail is a column. */
  const narrow = collapsed && beside
  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(COLLAPSED_KEY) === '1')
    } catch {
      /* Private window, or storage blocked. The rail simply starts open. */
    }
  }, [])
  const toggle = () => {
    setCollapsed((was) => {
      const now = !was
      try {
        localStorage.setItem(COLLAPSED_KEY, now ? '1' : '0')
      } catch {
        /* Not remembering it is survivable; not opening is not. */
      }
      return now
    })
  }

  /* Grouped, in the order the groups first appear, so the rail's shape is the
     rail's own decision and not a second list to keep in step. */
  const groups: { name: string | undefined; items: NavSection[] }[] = []
  for (const item of visible) {
    const last = groups[groups.length - 1]
    if (last && last.name === item.group) last.items.push(item)
    else groups.push({ name: item.group, items: [item] })
  }

  const item = (s: NavSection) => {
    const Icon = s.icon ? RAIL_ICONS[s.icon] : undefined
    const here = s.key === current
    const button = (
      <button
        key={s.key}
        type="button"
        aria-current={here ? 'page' : undefined}
        /**
         * Named even when there is no label to read.
         *
         * Collapsed, the rail is nine icon-only buttons. The `Tooltip` round
         * them is the right thing for a mouse and does **nothing** for a
         * screen reader — Radix describes a trigger, it does not name one —
         * so a narrowed rail announced nine buttons called nothing, and the
         * one way to move around the desk became unusable without sight.
         */
        {...(narrow ? { 'aria-label': s.label } : {})}
        onClick={() => onNavigate(s.key)}
        className={cn(
          'relative flex items-center rounded-md text-body outline-none transition-colors',
          'focus-visible:ring-2 focus-visible:ring-coral-fill/30 [&_svg]:size-4 [&_svg]:shrink-0',
          narrow ? 'h-10 w-10 justify-center' : 'h-10 gap-3 px-3',
          here
            ? 'bg-coral-soft font-medium text-coral-text'
            : 'text-ink-2 hover:bg-sunk hover:text-ink',
        )}
      >
        {/* The marker, so which screen you are on survives a glance and survives
            the rail being collapsed to forty pixels. The soft fill alone was not
            enough to tell at a distance. */}
        {here && (
          <span
            aria-hidden
            className="bg-coral-fill absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full"
          />
        )}
        {Icon ? <Icon /> : null}
        {!narrow && <span className="flex-1 text-left">{s.label}</span>}
        {!narrow && s.badge ? (
          <span className="bg-sunk text-micro text-ink-2 rounded-full px-2 tabular-nums">
            {s.badge}
          </span>
        ) : null}
        {/* Collapsed, a count has nowhere to sit, so it becomes a dot. Losing the
            number is better than losing the fact that something wants attention. */}
        {narrow && s.badge ? (
          <span
            aria-label={`${s.badge} need attention`}
            className="bg-coral-fill absolute right-1 top-1 size-2 rounded-full"
          />
        ) : null}
      </button>
    )
    return narrow ? (
      <Tooltip key={s.key} label={s.badge ? `${s.label} · ${s.badge}` : s.label}>
        {button}
      </Tooltip>
    ) : (
      button
    )
  }

  return (
    <TooltipProvider>
      {/* The shell is the screen and nothing outside it scrolls. `h-dvh` rather
          than `h-full`, because `h-full` only works while every ancestor has a
          height and one missing link makes the whole page scroll instead. */}
      <div className="flex h-dvh overflow-hidden">
        {/**
         * Past the rail, in one key.
         *
         * Twelve sections and a profile menu sit before the content on
         * every screen, so somebody working by keyboard tabbed through all
         * of them to reach a table — on every page, every time. The link is
         * invisible until it is focused, which is the whole convention:
         * the first Tab on the page offers the shortcut and nothing else
         * changes.
         */}
        <a
          href="#desk"
          className={cn(
            'bg-coral-fill text-on-coral sr-only rounded-md px-4 py-2 text-second',
            'focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60]',
          )}
        >
          Skip to the page
        </a>
        {/**
         * A scrim, and only below `lg`.
         *
         * The rail is 240px. On a 390px phone that is two thirds of the
         * screen, leaving about 150px for a table — so below `lg` the rail
         * stops taking space and slides over instead, and this is what closes
         * it again. `aria-hidden` and not focusable: the drawer traps nothing,
         * so Escape and the rail's own links are the ways out, and a tab stop
         * that looks like nothing is worse than no tab stop.
         */}
        {railOpen && (
          <button
            aria-label="Close the menu"
            onClick={() => setRailOpen(false)}
            className="bg-canvas/70 fixed inset-0 z-40 lg:hidden"
          />
        )}
        <nav
          aria-label="Sections"
          className={cn(
            'border-hairline bg-surface relative flex h-full shrink-0 flex-col border-r p-3 transition-[width]',
            /* Below `lg` it is off-canvas: fixed, over the content, and gone
               to the left until it is asked for. Above `lg` nothing about it
               changes, which is why every one of these is prefixed. */
            'fixed inset-y-0 left-0 z-50 w-60 transition-transform lg:static lg:z-auto lg:translate-x-0',
            railOpen ? 'translate-x-0' : '-translate-x-full',
            narrow ? 'lg:w-16 lg:items-center' : 'lg:w-60',
          )}
        >
          {/**
           * The logo, which becomes the mark alone when the rail narrows.
           *
           * It was the words "High Impact · Operations" set as an eyebrow —
           * the smallest, faintest type in the kit, used for the one thing on
           * the rail that says whose desk this is. The lockup here is the one
           * the prototype's sign-in panel uses: the mark, then the name in
           * the display face.
           *
           * The product name stays as live text beside the transparent PNG
           * mark so it remains sharp, localisable and accessible at every
           * rail width.
           *
           * The role moves underneath rather than trailing the name after a
           * middle dot: it is a fact about the person, not part of the logo,
           * and it was making the lockup read as one long label.
           */}
          <div className={cn('flex items-center gap-3 pb-3', narrow ? 'justify-center' : 'px-1')}>
            <img
              src="/brand/high-impact-logo-128.png"
              width={narrow ? 28 : 32}
              height={narrow ? 28 : 32}
              /* Empty: the accessible product name is already beside it. */
              alt=""
              className="shrink-0 object-contain"
            />
            {!narrow && (
              <span className="flex min-w-0 flex-col">
                <span className="text-title font-medium leading-tight truncate">High Impact</span>
                <Text as="span" variant="eyebrow" className="text-ink-muted truncate">
                  {user?.roleName ?? 'the desk'}
                </Text>
              </span>
            )}
          </div>

          {/**
           * The handle sits **on** the edge, half in and half out.
           *
           * It was a ghost button in the rail's own header, which put the
           * control that changes the rail's width inside the thing whose
           * width it changes — and at the top, nowhere near the border it
           * moves. On the edge and vertically centred it reads as a handle,
           * which is what it is.
           */}
          <Tooltip label={collapsed ? 'Widen the rail' : 'Narrow the rail'}>
            <button
              type="button"
              aria-label={collapsed ? 'Widen the rail' : 'Narrow the rail'}
              onClick={toggle}
              className={cn(
                /* Not on a phone: there is no column to narrow, and a handle
                   on the edge of a drawer offers to do something to a thing
                   that is already covering the page. */
                'hidden lg:grid',
                'absolute -right-3 top-1/2 z-10 size-6 -translate-y-1/2 place-items-center',
                'border-hairline bg-surface text-ink-muted rounded-full border',
                'hover:text-ink hover:border-hairline-strong shadow-e1',
                'focus-visible:ring-coral-fill/30 outline-none transition-colors focus-visible:ring-2',
                '[&_svg]:size-3.5',
              )}
            >
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </button>
          </Tooltip>

          {/* Only the middle scrolls. The heading above and the person below
              stay put, so signing out never requires scrolling to reach it —
              which is what happens on a short window once the rail has nine
              items, a heading and a footer in one scrolling column. */}
          <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1">
            {groups.map((g, i) => (
              <div key={g.name ?? `ungrouped-${i}`} className="flex flex-col gap-1">
                {/**
                 * A line, not a word.
                 *
                 * These were headings — "Today", "What we publish", "Running
                 * it" — and the client's answer to what they should say instead
                 * was **nothing**: the grouping is what carries the meaning, and
                 * three more pieces of text in a rail that is already nine
                 * items is what made it noisy. The same line at both widths, so
                 * narrowing the rail changes its size and not its shape.
                 */}
                {i > 0 && (
                  <div
                    role="presentation"
                    className={cn('bg-hairline my-2 h-px', collapsed ? 'w-6 self-center' : 'mx-3')}
                  />
                )}
                {g.items.map(item)}
              </div>
            ))}
          </div>

          {user && (
            /**
             * The person, and what they can do about being the person.
             *
             * Sign out was a full-width button sitting in the rail next to the
             * navigation, which put "leave" in the same list as "go to
             * Payments" — the one destructive thing on the rail, drawn like a
             * section. It is behind the name now, which is where somebody
             * looks for it, and the whole row is the trigger so a narrow rail
             * still has something to press.
             */
            <div className="border-hairline w-full border-t pt-3">
              <ProfileMenu
                user={user}
                theme={theme}
                onTheme={choose}
                onOpenProfile={onOpenProfile}
                onChangePassword={onChangePassword}
                onSignOut={onSignOut}
                trigger={
                  <button
                    type="button"
                    aria-label={`${user.fullName}, ${user.roleName}`}
                    className={cn(
                      'hover:bg-sunk flex w-full items-center rounded-md outline-none transition-colors',
                      'focus-visible:ring-coral-fill/30 focus-visible:ring-2',
                      collapsed ? 'justify-center p-1' : 'gap-3 p-2',
                    )}
                  >
                    <Avatar name={user.fullName} size="sm" tone="teal" />
                    {!narrow && (
                      <>
                        <span className="flex min-w-0 flex-1 flex-col text-left">
                          <Text as="span" variant="body-strong" className="truncate">
                            {user.fullName}
                          </Text>
                          <Text as="span" variant="caption">
                            {user.roleName}
                          </Text>
                        </span>
                        <ChevronsUpDown className="text-ink-muted size-4 shrink-0" aria-hidden />
                      </>
                    )}
                  </button>
                }
              />
            </div>
          )}
        </nav>

        {/**
         * The page does not scroll; what is on it decides what does.
         *
         * `overflow-y-auto` here made **the whole desk** the scrolling thing,
         * so a table of fifty rows put its own Next button two and a half
         * screens below the fold and the filters above it out of reach at the
         * same time. Said plainly, using it: *"to go to next page i have to
         * scroll so long to the button to click next such bad ux and the
         * whole page scrolling instead of table rows"*.
         *
         * `min-h-0` is the half that is easy to miss: without it a flex child
         * refuses to shrink below its content, so the table would still push
         * the page taller and nothing inside it would ever scroll.
         *
         * A screen that is genuinely taller than the window — a record, a
         * form — scrolls itself, because `ScrollingPage` below turns the
         * overflow back on for it.
         */}
        <main id="desk" tabIndex={-1} className="flex min-w-0 min-h-0 flex-1 flex-col">
          {/**
           * The phone's own top bar, which exists only to open the rail.
           *
           * Above `lg` the rail is always there and this would be a second
           * way to reach what is already on screen.
           */}
          <div className="border-hairline bg-surface flex shrink-0 items-center gap-3 border-b px-4 py-3 lg:hidden">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Open the menu"
              onClick={() => setRailOpen(true)}
            >
              <Menu />
            </Button>
            <img src="/brand/high-impact-logo-128.png" width={24} height={24} alt="" className="object-contain" />
            <span className="text-body-strong text-ink truncate">High Impact</span>
          </div>
          {/*
            Full width, which is the kit's own rule:

            ```css
            .fawn-desk { grid-template-columns: 240px 1fr; }
            .fawn-desk__main { padding: var(--fawn-space-9); }
            ```

            `1fr` and 32px of padding — no cap. This had `mx-auto max-w-7xl`,
            which the build invented: on anything wider than 1520px the desk
            stopped growing and sat centred with empty canvas either side,
            while the tables underneath it scrolled sideways inside their own
            wrappers. A table wants every pixel; a column of prose does not,
            and the two surfaces that are prose — a support message and the
            sheets — set their own reading width rather than making every
            table narrow to protect them.
          */}
          {/* No `gap` here any more: `PageBody` owns the spacing, because the
              scrolling half needs its padding **inside** the scroller. */}
          <div className="flex min-w-0 min-h-0 flex-1 flex-col p-4 sm:p-6 lg:p-8">{children}</div>
        </main>
      </div>
    </TooltipProvider>
  )
}

/** One column, centred — the sign-in screen and the full-page messages. */
/** Signing in, and anything else drawn outside the shell. Same reason for the provider. */
export function CenteredLayout({
  children,
  art,
  artTitle,
  artLine,
  width = 'narrow',
}: {
  children: React.ReactNode
  /** An illustration from `public/illustrations`, on the panel beside the form. */
  art?: string
  artTitle?: React.ReactNode
  artLine?: React.ReactNode
  /**
   * How wide the column is once the artwork panel has gone.
   *
   * `narrow` is a sign-in: two fields and a button, and anything wider makes a
   * short form look lost. `wide` is a form somebody fills in for ten minutes —
   * the professional's sign-up — where a 448px column turns a paragraph field
   * into a slot and puts the eye through twice as many lines.
   */
  width?: 'narrow' | 'wide'
}) {
  /**
   * Two panels, which is what the working prototype draws.
   *
   * The admin sign-in was built from the board on `03-getting-in`, which is a
   * 390-wide phone frame — and it is the **only** admin screen drawn at phone
   * size; 9.1 to 9.9 are all 1180. The working prototype has the real one: a
   * navy gradient panel on the left carrying the mark, the
   * `dashboard/admin-overview` drawing and the line about the desk, and the
   * form on the right at 56px of padding.
   *
   * The client found it by looking at the prototype. It was missed by reading
   * the boards and stopping at the first frame with the right name.
   *
   * Below `lg` the panel goes and the form takes the screen: the artwork is
   * the part that can be spared, and a 1180-wide design has to do something on
   * a laptop held sideways.
   */
  if (!art) {
    return (
      <TooltipProvider>
        {/* Same reason as below: centre it while it fits, not once it is
            taller than the window. */}
        <div className="grid min-h-full justify-items-center overflow-y-auto p-6">
          <div className={cn('my-auto w-full', width === 'wide' ? 'max-w-2xl' : 'max-w-md')}>
            {children}
          </div>
        </div>
      </TooltipProvider>
    )
  }
  return (
    <TooltipProvider>
      {/**
       * The whole window, not a card in the middle of one.
       *
       * The prototype frame is 1180×840 and it was read as a panel of that
       * size floating on a page — it is the **browser**, exactly as 390×844
       * elsewhere in the archive is the phone. Signing in is the only thing
       * this screen is for, so it takes the screen.
       */}
      <div className="flex h-dvh w-full overflow-hidden">
        <div
          className="hidden flex-[1.1] flex-col justify-between p-12 lg:flex"
          style={{
            background: 'linear-gradient(150deg, var(--color-navy), var(--color-navy-2))',
          }}
        >
          <div className="flex items-center gap-3">
            <img src="/brand/high-impact-logo-128.png" width={48} height={48} alt="" className="object-contain" />
            <span className="text-title text-on-navy font-medium">{artTitle}</span>
          </div>
          <img src={art} alt="" className="max-h-[320px] w-full object-contain" />
          {artLine && <p className="text-body text-on-navy max-w-md">{artLine}</p>}
        </div>
        {/**
         * `my-auto`, **not** `items-center`.
         *
         * Centring a flex child and letting the parent scroll is the oldest
         * trap in the box model: a child taller than the container is
         * centred on it, which puts its top *above* the scroll origin, and
         * no amount of scrolling reaches it. The authenticator step is the
         * tallest thing this screen ever draws — a QR code, ten recovery
         * codes and six digit boxes — and it was cut off at the top with no
         * way back up. Found by the client on their own first sign-in.
         *
         * `my-auto` centres it while it fits and does nothing once it does
         * not, which is the behaviour anybody wanted from `items-center`.
         */}
        <div className="flex min-w-0 flex-1 justify-center overflow-y-auto p-8 sm:p-14">
          {/* The form keeps a readable measure however wide the window is. */}
          <div className="my-auto w-full max-w-[420px]">{children}</div>
        </div>
      </div>
    </TooltipProvider>
  )
}

/**
 * What scrolls: the page, or something inside it.
 *
 * The shell stopped scrolling so a list screen could hand its height to its
 * table and pin the Next button where somebody can reach it. That is right for
 * a list and wrong for everything else — a record, a long form, the settings
 * are panels stacked down the page and want the whole thing to scroll.
 *
 * One switch rather than a guess, set where the screens are chosen, because
 * the same section is a list on one route and a record on the next: Members is
 * a table until somebody opens one.
 *
 * `-m-8 … p-8` on the scrolling half so the padding scrolls with the content
 * rather than sitting outside it, which would put a gap above the scrollbar.
 */
export function PageBody({ fills, children }: { fills: boolean; children: React.ReactNode }) {
  return (
    <div
      className={
        fills
          ? /**
             * **Only a laptop pins the furniture.**
             *
             * Filling the window means the heading, the figures and the
             * filters keep their height and only the rows move. That is right
             * where there is height to spend. On a 390×760 phone the same
             * arrangement spends about 250px of it on chrome and leaves the
             * table a slot — said plainly: *"in mobile screen the page header
             * and all is taking so much height the table is way below"*.
             *
             * Below `lg` the page scrolls as one thing instead, so the
             * heading rolls away and the table gets the whole screen.
             *
             * The negative margin puts the scrollbar at the window's edge
             * rather than inside the padding, and tracks `main`'s own padding
             * at every step — it was `-m-8 p-8` against a `p-4` phone, which
             * is a negative margin larger than the padding it undoes.
             */
            /*
             * **Block, not flex, below `lg`.** A flex item whose `overflow`
             * is anything but `visible` gets `min-height: 0`, so the table —
             * which is `overflow-x-auto` — collapsed to whatever height was
             * left and scrolled *inside itself* again, which is the thing
             * being undone. As a block it takes its natural height and the
             * page scrolls past it.
             */
            '-m-4 flex-1 space-y-4 overflow-y-auto p-4 sm:-m-6 sm:p-6 lg:m-0 lg:flex lg:min-h-0 lg:flex-col lg:space-y-0 lg:gap-6 lg:overflow-visible lg:p-0'
          : '-m-4 flex-1 overflow-y-auto p-4 sm:-m-6 sm:p-6 lg:-m-8 lg:p-8'
      }
    >
      {children}
    </div>
  )
}
